import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import SidebarLayout from '../components/SidebarLayout';
import StatCard from '../components/StatCard';
import UserAvatar from '../components/UserAvatar';
import ServerStorageCard from '../components/ServerStorageCard'; // Ensure this path is correct
import adminService from '../services/adminService';
import settingsService from '../services/settingsService';
import uploadSettingsService from '../services/uploadSettingsService';
import { formatFileSize, formatDate } from '../utils/helpers';
import { cn } from '../lib/utils';

// Shadcn UI Components
import { Button } from "../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../components/ui/card"
import { Input } from "../components/ui/input"
import { Label } from "../components/ui/label"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select"
import { Badge } from "../components/ui/badge"
import { Progress } from "../components/ui/progress"
import { useToast } from "../components/ui/use-toast"
import { Textarea } from "../components/ui/textarea"
import { ScrollArea } from "../components/ui/scroll-area"

// Icons
import {
  Users,
  Folder,
  FileText,
  Hourglass,
  Trash2,
  Eye,
  Edit2,
  Cloud,
  CheckCircle2,
  XCircle,
  UserPlus,
  Ban,
  Clock,
  TrendingUp,
  Upload,
  FolderOpen,
  Lock,
  Settings,
  Image as ImageIcon,
  UploadCloud,
  LayoutDashboard,
  Shield,
  History,
  FilePlus,
  Search,
  ChevronRight,
  ArrowRight
} from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  // Dialog States
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [editStorageDialogOpen, setEditStorageDialogOpen] = useState(false);
  const [newStorageLimit, setNewStorageLimit] = useState('');
  const [savingStorage, setSavingStorage] = useState(false);

  // Reset Password States
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);

  // Settings States
  const [appSettings, setAppSettings] = useState(null);
  const [settingsFormData, setSettingsFormData] = useState({ app_name: '', app_subtitle: '', default_view_mode: 'compact' });
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Activity States
  const [userActivities, setUserActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  // Upload Settings States
  const [uploadSettings, setUploadSettings] = useState(null);
  const [maxFileSize, setMaxFileSize] = useState(100);
  const [selectedExtensions, setSelectedExtensions] = useState([]);
  const [customExtension, setCustomExtension] = useState('');
  const [defaultCategories, setDefaultCategories] = useState({});
  const [savingUploadSettings, setSavingUploadSettings] = useState(false);

  // Tab State
  const [activeTab, setActiveTab] = useState('dashboard');

  // Users pagination
  const [userPage, setUserPage] = useState(1);
  const USERS_PER_PAGE = 5;
  const userTotalPages = Math.max(1, Math.ceil(users.length / USERS_PER_PAGE));
  const pagedUsers = users.slice((userPage - 1) * USERS_PER_PAGE, userPage * USERS_PER_PAGE);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadData();
    loadActivities();
    loadUploadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  // ============================================
  // DATA LOADING
  // ============================================
  const loadData = async () => {
    try {
      setLoading(true);
      const results = await Promise.allSettled([
        adminService.getDashboardStats(),
        adminService.getAllUsers(),
        adminService.getPendingRegistrations(),
        settingsService.getSettings()
      ]);

      const [statsResult, usersResult, pendingResult, settingsResult] = results;

      if (statsResult.status === 'fulfilled') setStats(statsResult.value.stats);
      else toast({ variant: "destructive", title: "Gagal memuat statistik", description: statsResult.reason?.message });

      if (usersResult.status === 'fulfilled') {
        const usersList = usersResult.value.users || [];
        setUsers(usersList);
        const sortedUsers = [...usersList].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setRecentUsers(sortedUsers.slice(0, 5));
      } else toast({ variant: "destructive", title: "Gagal memuat pengguna", description: usersResult.reason?.message });

      if (pendingResult.status === 'fulfilled') setPendingUsers(pendingResult.value.pending_users || []);

      if (settingsResult.status === 'fulfilled') {
        const settingsData = settingsResult.value;
        setAppSettings(settingsData);
        setSettingsFormData({
          app_name: settingsData.app_name,
          app_subtitle: settingsData.app_subtitle,
          default_view_mode: settingsData.default_view_mode
        });
        setLogoPreview(settingsData.logo_url);
      }
    } catch (err) {
      console.error('Unexpected error loading admin data:', err);
      toast({ variant: "destructive", title: "Error Sistem", description: "Terjadi kesalahan saat memuat data." });
    } finally {
      setLoading(false);
    }
  };

  const loadActivities = async () => {
    try {
      setActivitiesLoading(true);
      const activitiesData = await adminService.getRecentActivities();
      setUserActivities(activitiesData.user_activities || []);
    } catch (err) {
      console.error('Error loading activities:', err);
    } finally {
      setActivitiesLoading(false);
    }
  };

  const loadUploadSettings = async () => {
    try {
      const [settings, categories] = await Promise.all([
        uploadSettingsService.getSettings(),
        uploadSettingsService.getDefaultExtensions()
      ]);
      setUploadSettings(settings);
      setMaxFileSize(settings.max_file_size_mb);
      setSelectedExtensions(settings.allowed_extensions_array || []);
      setDefaultCategories(categories);
    } catch (err) {
      console.error('Error loading upload settings:', err);
    }
  };

  // ============================================
  // ACTIONS
  // ============================================

  // Delete User
  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await adminService.deleteUser(selectedUser.id);
      toast({ title: "Berhasil", description: "Pengguna berhasil dihapus" });
      setDeleteDialogOpen(false);
      loadData();
    } catch (err) {
      toast({ variant: "destructive", title: "Gagal", description: err.response?.data?.message || "Gagal menghapus pengguna" });
    }
  };

  // Approve User
  const handleApproveClick = (user) => {
    setSelectedUser(user);
    setApproveDialogOpen(true);
  };

  const handleApproveConfirm = async () => {
    try {
      await adminService.approveUser(selectedUser.id);
      toast({ title: "Berhasil", description: `Pengguna ${selectedUser.username} berhasil disetujui` });
      setApproveDialogOpen(false);
      setSelectedUser(null);
      loadData();
    } catch (err) {
      toast({ variant: "destructive", title: "Gagal", description: err.response?.data?.message || "Gagal menyetujui pengguna" });
    }
  };

  // Reject User
  const handleRejectClick = (user) => {
    setSelectedUser(user);
    setRejectDialogOpen(true);
    setRejectionReason('');
  };

  const handleRejectConfirm = async () => {
    if (!rejectionReason.trim()) {
      toast({ variant: "destructive", title: "Error", description: "Alasan penolakan harus diisi" });
      return;
    }
    try {
      await adminService.rejectUser(selectedUser.id, rejectionReason);
      toast({ title: "Berhasil", description: `Pengguna ${selectedUser.username} berhasil ditolak` });
      setRejectDialogOpen(false);
      setSelectedUser(null);
      setRejectionReason('');
      loadData();
    } catch (err) {
      toast({ variant: "destructive", title: "Gagal", description: err.response?.data?.message || "Gagal menolak pengguna" });
    }
  };

  // Edit Storage
  const handleEditStorageClick = (user) => {
    setSelectedUser(user);
    const currentLimitGB = (user.storage_limit / (1024 ** 3)).toFixed(0);
    setNewStorageLimit(currentLimitGB);
    setEditStorageDialogOpen(true);
  };

  const handleSaveStorageLimit = async () => {
    if (parseFloat(newStorageLimit) <= 0) {
      toast({ variant: "destructive", title: "Error", description: "Batas penyimpanan harus lebih dari 0" });
      return;
    }
    try {
      setSavingStorage(true);
      await adminService.updateUserStorage(selectedUser.id, parseFloat(newStorageLimit));
      toast({ title: "Berhasil", description: "Batas penyimpanan berhasil diperbarui" });
      setEditStorageDialogOpen(false);
      loadData();
    } catch (err) {
      toast({ variant: "destructive", title: "Gagal", description: err.response?.data?.message || "Gagal memperbarui penyimpanan" });
    } finally {
      setSavingStorage(false);
    }
  };

  // Reset Password
  const handleResetPasswordClick = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setResetPasswordDialogOpen(true);
  };

  const handleResetPasswordConfirm = async () => {
    if (newPassword !== confirmPassword) {
      toast({ variant: "destructive", title: "Error", description: "Password tidak cocok!" });
      return;
    }
    if (newPassword.length < 6) {
      toast({ variant: "destructive", title: "Error", description: "Password minimal 6 karakter" });
      return;
    }
    setResettingPassword(true);
    try {
      await adminService.resetUserPassword(selectedUser.id, newPassword);
      toast({ title: "Berhasil", description: `Password untuk ${selectedUser.username} berhasil direset` });
      setResetPasswordDialogOpen(false);
    } catch (error) {
      toast({ variant: "destructive", title: "Gagal", description: error.response?.data?.message || "Gagal mereset password" });
    } finally {
      setResettingPassword(false);
    }
  };

  // Settings Handlers
  const handleSettingsChange = (field, value) => {
    setSettingsFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast({ variant: "destructive", title: "Error", description: "Hanya file gambar yang diperbolehkan" });
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        toast({ variant: "destructive", title: "Error", description: "Ukuran file maksimal 2MB" });
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);
      await settingsService.updateSettings(settingsFormData);
      if (logoFile) {
        setUploadingLogo(true);
        const result = await settingsService.uploadLogo(logoFile);
        setLogoPreview(result.logo_url);
        setLogoFile(null);
      }
      toast({ title: "Berhasil", description: "Pengaturan berhasil disimpan" });
      const updatedSettings = await settingsService.getSettings();
      setAppSettings(updatedSettings);
    } catch (error) {
      toast({ variant: "destructive", title: "Gagal", description: error.response?.data?.message || "Gagal menyimpan pengaturan" });
    } finally {
      setSavingSettings(false);
      setUploadingLogo(false);
    }
  };

  // Upload Settings Handlers
  const handleToggleCategory = (category) => {
    const categoryExtensions = defaultCategories[category] || [];
    const allSelected = categoryExtensions.every(ext => selectedExtensions.includes(ext));
    if (allSelected) {
      setSelectedExtensions(selectedExtensions.filter(ext => !categoryExtensions.includes(ext)));
    } else {
      const newExtensions = [...new Set([...selectedExtensions, ...categoryExtensions])];
      setSelectedExtensions(newExtensions);
    }
  };

  const handleAddCustomExtension = () => {
    const ext = customExtension.trim().toLowerCase();
    if (!ext || !ext.startsWith('.')) return toast({ variant: "destructive", title: "Error", description: "Ekstensi harus dimulai dengan titik (.)" });
    if (selectedExtensions.includes(ext)) return toast({ variant: "destructive", title: "Warning", description: "Ekstensi sudah ada" });
    setSelectedExtensions([...selectedExtensions, ext]);
    setCustomExtension('');
  };

  const handleSaveUploadSettings = async () => {
    if (maxFileSize < 1 || maxFileSize > 10240) return toast({ variant: "destructive", title: "Error", description: "Ukuran file harus antara 1-10240 MB" });
    if (selectedExtensions.length === 0) return toast({ variant: "destructive", title: "Error", description: "Minimal satu ekstensi harus dipilih" });
    try {
      setSavingUploadSettings(true);
      await uploadSettingsService.updateSettings({
        max_file_size_mb: parseInt(maxFileSize),
        allowed_extensions: selectedExtensions
      });
      toast({ title: "Berhasil", description: "Pengaturan upload berhasil disimpan" });
    } catch (error) {
      toast({ variant: "destructive", title: "Gagal", description: error.response?.data?.message || "Gagal menyimpan pengaturan" });
    } finally {
      setSavingUploadSettings(false);
    }
  };

  // Helpers
  const getStoragePercentage = (used, limit) => {
    const usedNum = parseFloat(used) || 0;
    const limitNum = parseFloat(limit) || 1;
    if (limitNum === 0) return 0;
    return Math.min(Math.max((usedNum / limitNum) * 100, 0), 100);
  };

  const getTotalStorageUsed = () => users.reduce((acc, user) => acc + (parseFloat(user.total_size) || 0), 0);
  const getTotalStorageAllocated = () => users.reduce((acc, user) => acc + (parseFloat(user.storage_limit) || 0), 0);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved': return <Badge className="bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-300">Disetujui</Badge>;
      case 'pending': return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-300">Menunggu</Badge>;
      case 'rejected': return <Badge className="bg-red-100 text-red-800 hover:bg-red-100 dark:bg-red-900 dark:text-red-300">Ditolak</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <SidebarLayout storageData={null}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
          <div style={{ width: '48px', height: '48px', borderRadius: '50%', border: '3px solid transparent', borderTopColor: '#3b82f6', animation: 'spin 0.8s linear infinite' }} />
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout storageData={null}>
      <div>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Shield className="h-8 w-8 text-primary" />
              Admin Dashboard
            </h1>
            <p className="text-muted-foreground mt-1">
              Kelola pengguna, penyimpanan, dan pengaturan sistem.
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate('/dashboard')} className="gap-2">
            <Cloud className="h-4 w-4" />
            Drive Saya
          </Button>
        </div>

        {pendingUsers.length > 0 && (
          <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-full text-yellow-600 dark:text-yellow-400">
                <UserPlus className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-yellow-900 dark:text-yellow-200">Registrasi Pending</h3>
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  Ada {pendingUsers.length} pengguna menunggu persetujuan Anda.
                </p>
              </div>
            </div>
            <Button size="sm" variant="secondary" onClick={() => setActiveTab('pending')}>
              Tinjau
            </Button>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white dark:bg-slate-950 border p-1 h-auto flex-wrap justify-start">
            <TabsTrigger value="dashboard" className="gap-2"><LayoutDashboard className="h-4 w-4" /> Dashboard</TabsTrigger>
            <TabsTrigger value="users" className="gap-2">
              <Users className="h-4 w-4" /> Semua Pengguna
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">{users.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="pending" className="gap-2">
              <Hourglass className="h-4 w-4" /> Pending
              {pendingUsers.length > 0 && <Badge variant="destructive" className="ml-1 h-5 px-1.5">{pendingUsers.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="activities" className="gap-2"><History className="h-4 w-4" /> Aktivitas</TabsTrigger>
            <TabsTrigger value="settings" className="gap-2"><Settings className="h-4 w-4" /> Pengaturan</TabsTrigger>
            <TabsTrigger value="upload-settings" className="gap-2"><UploadCloud className="h-4 w-4" /> Upload</TabsTrigger>
          </TabsList>

          {/* DASHBOARD TAB */}
          <TabsContent value="dashboard" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard title="Total Pengguna" value={stats?.totalUsers || 0} icon={<Users className="h-4 w-4" />} color="primary" />
              <StatCard title="Total Folder" value={stats?.totalFolders || 0} icon={<Folder className="h-4 w-4" />} color="yellow" />
              <StatCard title="Total File" value={stats?.totalFiles || 0} icon={<FileText className="h-4 w-4" />} color="green" />
              <StatCard title="Pengguna Pending" value={stats?.pendingUsers || 0} icon={<Hourglass className="h-4 w-4" />} color="red" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="h-full">
                <ServerStorageCard />
              </div>
              {/* Ringkasan Penyimpanan User — styled like ServerStorageCard */}
              {(() => {
                const used = getTotalStorageUsed();
                const allocated = getTotalStorageAllocated();
                const pct = allocated > 0 ? Math.min((used / allocated) * 100, 100) : 0;
                const isCritical = pct > 90;
                const isWarning = pct > 75;
                const barColor = isCritical ? 'bg-red-500' : isWarning ? 'bg-yellow-500' : 'bg-blue-600';
                const avgUsed = used / (users.length || 1);
                const avgQuota = allocated / (users.length || 1);
                return (
                  <Card className="h-full flex flex-col">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-purple-100 text-purple-600 rounded-lg">
                            <Cloud className="h-5 w-5" />
                          </div>
                          <div>
                            <CardTitle className="text-base font-semibold">Ringkasan Penyimpanan User</CardTitle>
                            <p className="text-xs text-muted-foreground">{users.length} pengguna terdaftar</p>
                          </div>
                        </div>
                        {isWarning && (
                          <div className={cn(
                            "px-2 py-1 rounded text-xs font-bold flex items-center gap-1",
                            isCritical ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"
                          )}>
                            {isCritical ? 'KRITIS' : 'PERINGATAN'}
                          </div>
                        )}
                      </div>
                    </CardHeader>

                    <CardContent className="flex-1 flex flex-col justify-between pt-0 pb-6">
                      {/* Big percentage */}
                      <div className="mb-3 mt-2">
                        <div className="flex items-baseline gap-1 mb-1">
                          <span className="text-4xl font-bold tracking-tight">{pct.toFixed(1)}%</span>
                          <span className="text-sm text-muted-foreground font-medium">Terpakai</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          dari total kuota yang dialokasikan ke semua user
                        </p>
                      </div>

                      {/* Progress bar */}
                      <div className="space-y-3 mb-4">
                        <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${barColor}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>

                        {/* Terpakai / Total row */}
                        <div className="flex justify-between items-center bg-secondary/50 p-2 rounded-md text-sm">
                          <div>
                            <span className="text-xs text-muted-foreground block">Total Terpakai</span>
                            <span className="font-semibold">{formatFileSize(used)}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-xs text-muted-foreground block">Total Dialokasikan</span>
                            <span className="font-semibold">{allocated > 0 ? formatFileSize(allocated) : '—'}</span>
                          </div>
                        </div>
                      </div>

                      {/* File type breakdown bar */}
                      {stats?.fileTypes?.some(t => t.total_size > 0) && (
                        <div className="mb-3">
                          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden flex mb-2">
                            {stats.fileTypes.map((type, i) => {
                              const p = used > 0 ? (type.total_size / used) * 100 : 0;
                              const clr = { PDF: 'bg-red-500', Images: 'bg-emerald-500', Videos: 'bg-amber-500', Audio: 'bg-purple-500', Documents: 'bg-blue-500', Archives: 'bg-stone-500' }[type.type] || 'bg-gray-400';
                              return p > 0 ? <div key={i} className={`h-full ${clr}`} style={{ width: `${p}%` }} title={`${type.type}: ${formatFileSize(type.total_size)}`} /> : null;
                            })}
                          </div>
                          <div className="grid grid-cols-2 gap-1 text-xs">
                            {stats.fileTypes.filter(t => t.total_size > 0).map((type, i) => {
                              const clr = { PDF: 'bg-red-500', Images: 'bg-emerald-500', Videos: 'bg-amber-500', Audio: 'bg-purple-500', Documents: 'bg-blue-500', Archives: 'bg-stone-500' }[type.type] || 'bg-gray-400';
                              return (
                                <div key={i} className="flex items-center gap-1.5">
                                  <div className={`w-2 h-2 rounded-full shrink-0 ${clr}`} />
                                  <span className="text-gray-500 truncate">{type.type}</span>
                                  <span className="text-gray-400 ml-auto shrink-0">{formatFileSize(type.total_size)}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Average stats */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-secondary/50 p-3 rounded-lg text-center">
                          <div className="text-base font-bold text-primary">{formatFileSize(avgUsed)}</div>
                          <div className="text-xs text-muted-foreground">Rata-rata Terpakai</div>
                        </div>
                        <div className="bg-secondary/50 p-3 rounded-lg text-center">
                          <div className="text-base font-bold text-primary">{avgQuota > 0 ? formatFileSize(avgQuota) : '—'}</div>
                          <div className="text-xs text-muted-foreground">Rata-rata Kuota</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}
            </div>
          </TabsContent>

          {/* USERS TAB */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Daftar Pengguna</CardTitle>
                    <CardDescription>Kelola semua pengguna terdaftar dalam sistem.</CardDescription>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {users.length} pengguna total
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Pengguna</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>File</TableHead>
                      <TableHead>Penyimpanan</TableHead>
                      <TableHead>Kuota</TableHead>
                      <TableHead className="text-right">Aksi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pagedUsers.map((u) => (
                      <TableRow key={u.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <UserAvatar username={u.username} size="medium" />
                            <div>
                              <div className="font-medium">{u.username}</div>
                              <div className="text-xs text-muted-foreground">{u.email}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(u.account_status)}</TableCell>
                        <TableCell>
                          <div className="font-medium">{u.total_files || 0}</div>
                          <div className="text-xs text-muted-foreground">files</div>
                        </TableCell>
                        <TableCell className="w-[200px]">
                          <div className="flex justify-between text-xs mb-1">
                            <span className="font-medium">{formatFileSize(u.total_size || 0)}</span>
                            <span className="text-muted-foreground">{getStoragePercentage(u.total_size, u.storage_limit).toFixed(0)}%</span>
                          </div>
                          <Progress value={getStoragePercentage(u.total_size, u.storage_limit)} className="h-2" />
                        </TableCell>
                        <TableCell className="font-medium">{formatFileSize(u.storage_limit)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleEditStorageClick(u)} title="Edit Storage">
                              <Cloud className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleResetPasswordClick(u)} title="Reset Password">
                              <Lock className="h-4 w-4 text-orange-500" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteClick(u)} title="Hapus User">
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>

              {/* Pagination */}
              {userTotalPages > 1 && (
                <div className="border-t px-6 py-3 flex items-center gap-3">
                  {/* Nav buttons — LEFT */}
                  <div className="flex items-center gap-1">
                    <button
                      disabled={userPage === 1}
                      onClick={() => setUserPage(p => Math.max(1, p - 1))}
                      style={{ height: 32, width: 32, borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: userPage === 1 ? 'not-allowed' : 'pointer', opacity: userPage === 1 ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#374151' }}
                    >‹</button>

                    {Array.from({ length: userTotalPages }, (_, i) => i + 1).map(page => {
                      const show = page === 1 || page === userTotalPages || Math.abs(page - userPage) <= 1;
                      if (page === userPage - 2 && userPage > 3) return <span key={page} style={{ width: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>…</span>;
                      if (page === userPage + 2 && userPage < userTotalPages - 2) return <span key={page} style={{ width: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>…</span>;
                      if (!show) return null;
                      return (
                        <button key={page} onClick={() => setUserPage(page)} style={{ height: 32, width: 32, borderRadius: 6, border: '1px solid', cursor: 'pointer', borderColor: page === userPage ? '#3b82f6' : '#e2e8f0', background: page === userPage ? '#3b82f6' : '#fff', color: page === userPage ? '#fff' : '#374151', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {page}
                        </button>
                      );
                    })}

                    <button
                      disabled={userPage === userTotalPages}
                      onClick={() => setUserPage(p => Math.min(userTotalPages, p + 1))}
                      style={{ height: 32, width: 32, borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: userPage === userTotalPages ? 'not-allowed' : 'pointer', opacity: userPage === userTotalPages ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#374151' }}
                    >›</button>
                  </div>

                  {/* Info — RIGHT */}
                  <span className="text-sm text-muted-foreground ml-auto">
                    Hal. <span className="font-semibold text-gray-700">{userPage}</span> dari{' '}
                    <span className="font-semibold text-gray-700">{userTotalPages}</span>
                    &nbsp;·&nbsp;
                    <span className="font-semibold text-gray-700">{users.length}</span> pengguna
                  </span>
                </div>
              )}
            </Card>

          </TabsContent>

          {/* PENDING TAB */}
          <TabsContent value="pending">
            <Card>
              <CardHeader>
                <CardTitle>Registrasi Menunggu Persetujuan</CardTitle>
                <CardDescription>Tinjau pengguna baru yang mendaftar.</CardDescription>
              </CardHeader>
              <CardContent>
                {pendingUsers.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                    <h3 className="text-lg font-medium">Semua Bersih!</h3>
                    <p className="text-muted-foreground">Tidak ada registrasi pending saat ini.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Pengguna</TableHead>
                        <TableHead>Waktu Registrasi</TableHead>
                        <TableHead className="text-right">Aksi</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingUsers.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <UserAvatar username={u.username} size="medium" color="warning" />
                              <div>
                                <div className="font-medium">{u.username}</div>
                                <div className="text-xs text-muted-foreground">{u.email}</div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{formatDate(u.created_at)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleRejectClick(u)}>
                                <XCircle className="h-4 w-4 mr-1" /> Tolak
                              </Button>
                              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleApproveClick(u)}>
                                <CheckCircle2 className="h-4 w-4 mr-1" /> Setujui
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ACTIVITIES TAB */}
          {/* ACTIVITIES TAB */}
          <TabsContent value="activities">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {activitiesLoading ? (
                <div className="col-span-2 flex justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : userActivities.length === 0 ? (
                <div className="col-span-2 text-center py-12">
                  <History className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                  <p className="text-muted-foreground">Belum ada aktivitas tercatat.</p>
                </div>
              ) : (
                userActivities.map((userActivity, userIndex) => (
                  <Card key={userIndex} className="overflow-hidden">
                    <CardHeader className="bg-muted/50 pb-3">
                      <div className="flex items-center gap-3">
                        <UserAvatar username={userActivity.username || 'System'} size="medium" />
                        <div>
                          <CardTitle className="text-base">{userActivity.username || 'System'}</CardTitle>
                          <CardDescription className="text-xs">{userActivity.email}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="divide-y">
                        {userActivity.activities.map((activity, index) => (
                          <div key={index} className="p-3 flex items-start gap-3 hover:bg-muted/50 transition-colors">
                            <div className="mt-0.5">
                              <Badge variant="secondary" className="text-[10px] px-1 py-0 h-5 font-normal">
                                {activity.action || 'Unknown'}
                              </Badge>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">
                                {activity.target_name || activity.description || '-'}
                              </p>
                              <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {formatDate(activity.created_at)}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* SETTINGS TAB */}
          <TabsContent value="settings">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Identitas Aplikasi</CardTitle>
                  <CardDescription>Ubah nama dan deskripsi aplikasi.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nama Aplikasi</Label>
                    <Input
                      value={settingsFormData.app_name}
                      onChange={(e) => handleSettingsChange('app_name', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Subtitle</Label>
                    <Input
                      value={settingsFormData.app_subtitle}
                      onChange={(e) => handleSettingsChange('app_subtitle', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Mode Tampilan Default</Label>
                    <Select
                      value={settingsFormData.default_view_mode}
                      onValueChange={(val) => handleSettingsChange('default_view_mode', val)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Pilih mode" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="list">List View</SelectItem>
                        <SelectItem value="compact">Compact View</SelectItem>
                        <SelectItem value="grid">Grid View</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">Ini akan menjadi default untuk user baru.</p>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button onClick={handleSaveSettings} disabled={savingSettings}>
                    {savingSettings ? 'Menyimpan...' : 'Simpan Perubahan'}
                  </Button>
                </CardFooter>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Logo Aplikasi</CardTitle>
                  <CardDescription>Upload logo untuk header dan halaman login.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center bg-gray-50 dark:bg-slate-900 mb-4 h-48">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo Preview" className="max-h-full object-contain" />
                    ) : (
                      <div className="text-center text-muted-foreground">
                        <ImageIcon className="h-10 w-10 mx-auto mb-2 opacity-50" />
                        <p>Belum ada logo</p>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-center">
                    <Button variant="outline" className="relative">
                      Pilih File
                      <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" accept="image/*" onChange={handleLogoFileChange} />
                    </Button>
                  </div>
                  {logoFile && <p className="text-sm text-center mt-2 text-green-600">Terpilih: {logoFile.name}</p>}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* UPLOAD SETTINGS TAB */}
          <TabsContent value="upload-settings">
            <Card>
              <CardHeader>
                <CardTitle>Pengaturan Upload</CardTitle>
                <CardDescription>Batasi ukuran dan jenis file yang diizinkan.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="max-w-md space-y-2">
                  <Label>Maksimal Ukuran File (MB)</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      value={maxFileSize}
                      onChange={(e) => setMaxFileSize(e.target.value)}
                    />
                    <div className="flex items-center text-sm font-medium">MB</div>
                  </div>
                </div>

                <div className="space-y-4">
                  <Label>Ekstensi File ({selectedExtensions.length})</Label>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {Object.keys(defaultCategories).map((category) => (
                      <Badge
                        key={category}
                        variant="outline"
                        className="cursor-pointer hover:bg-secondary"
                        onClick={() => handleToggleCategory(category)}
                      >
                        {category.charAt(0).toUpperCase() + category.slice(1)} ({defaultCategories[category].length})
                      </Badge>
                    ))}
                  </div>

                  <div className="border rounded-md p-4 bg-secondary/20 min-h-[100px]">
                    {selectedExtensions.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {selectedExtensions.map((ext) => (
                          <Badge key={ext} className="bg-primary/10 text-primary hover:bg-primary/20 border-0">
                            {ext}
                            <span
                              className="ml-1 cursor-pointer hover:text-red-500"
                              onClick={() => setSelectedExtensions(selectedExtensions.filter(e => e !== ext))}
                            >×</span>
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">Tidak ada ekstensi dipilih. User tidak bisa upload file.</p>
                    )}
                  </div>

                  <div className="flex gap-2 max-w-sm">
                    <Input
                      placeholder=".custom"
                      value={customExtension}
                      onChange={(e) => setCustomExtension(e.target.value)}
                    />
                    <Button onClick={handleAddCustomExtension} variant="secondary">Tambah</Button>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button onClick={handleSaveUploadSettings} disabled={savingUploadSettings}>
                  {savingUploadSettings ? <div className="animate-spin mr-2 h-4 w-4 border-b-2 border-white rounded-full"></div> : <UploadCloud className="h-4 w-4 mr-2" />}
                  Simpan Aturan Upload
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* DIALOGS */}

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <Trash2 className="h-5 w-5" /> Hapus Pengguna
            </DialogTitle>
            <DialogDescription>
              Apakah Anda yakin ingin menghapus <strong>{selectedUser?.username}</strong>?
              Semua data dan file pengguna ini akan dihapus permanen.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteDialogOpen(false)}>Batal</Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>Ya, Hapus</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-green-600 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5" /> Setujui Pengguna
            </DialogTitle>
            <DialogDescription>
              Berikan akses masuk kepada <strong>{selectedUser?.username}</strong> ({selectedUser?.email})?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setApproveDialogOpen(false)}>Batal</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleApproveConfirm}>Setujui</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Ban className="h-5 w-5" /> Tolak Pengguna
            </DialogTitle>
            <DialogDescription>
              Tolak pendaftaran <strong>{selectedUser?.username}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Alasan Penolakan</Label>
            <Textarea
              placeholder="Contoh: Email tidak valid"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectDialogOpen(false)}>Batal</Button>
            <Button variant="destructive" onClick={handleRejectConfirm}>Tolak Pendaftaran</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Storage Dialog */}
      <Dialog open={editStorageDialogOpen} onOpenChange={setEditStorageDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Batas Penyimpanan</DialogTitle>
            <DialogDescription>
              Ubah kuota penyimpanan untuk <strong>{selectedUser?.username}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-2">
              <Label>Batas Baru (GB)</Label>
              <Input
                type="number"
                value={newStorageLimit}
                onChange={(e) => setNewStorageLimit(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Batas saat ini: {selectedUser && formatFileSize(selectedUser.storage_limit)}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditStorageDialogOpen(false)}>Batal</Button>
            <Button onClick={handleSaveStorageLimit} disabled={savingStorage}>
              {savingStorage ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password User</DialogTitle>
            <DialogDescription>
              Set password baru untuk <strong>{selectedUser?.username}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Password Baru</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Konfirmasi Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResetPasswordDialogOpen(false)}>Batal</Button>
            <Button onClick={handleResetPasswordConfirm} disabled={resettingPassword}>
              {resettingPassword ? 'Mereset...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </SidebarLayout>
  );
};

export default AdminDashboard;
